import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Check, X } from 'lucide-react';
import Table from '../../base_component/Table/Table';
import Pagination from '../../base_component/Pagination/Pagination';
import FormSelect from '../../base_component/FormSelect/FormSelect';
import Dialog from '../../base_component/Dialog/Dialog';
import { useNavigate } from 'react-router-dom';
import Skeleton from 'react-loading-skeleton';
import moment from 'moment';
import toast from 'react-hot-toast';
import { checkoutSubscriptionAPI, getCheckoutSubscriptionPlanAPI, getInvoicesAPI } from '../../service/ApiServices';
import { BsInfoCircleFill } from "react-icons/bs";
import EllipsisLoader from '../../base_component/EllipsisLoader/EllipsisLoader';

export default function Billing() {
    const navigate = useNavigate()
    const [currentPage, setCurrentPage] = useState(1);
    const [currentInvoices, setCurrentInvoices] = useState([]);
    const [pageCount, setPageCount] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(5);
    const [cancelPlan, setCancelPlan] = useState(false)
    const [showInfoModal, setShowInfoModal] = useState(false)
    const [currentPlanData, setCurrentPlanData] = useState([]);
    const [selectedPlanInfo, setSelectedPlanInfo] = useState(null)
    const [selectedPlan, setSelectedPlan] = useState(null)
    const [isPaymentLoading, setIsPaymentLoading] = useState(false)
    const [loading, setLoading] = useState(true);
    const [plan, setPlan] = useState(localStorage.getItem("plan"))
    const startDate = localStorage.getItem("plan_start_date")
    const endDate = localStorage.getItem("plan_end_date")
    const userId = localStorage.getItem("hoast_user_id");

    useEffect(() => {
        const handleStorageChange = () => {
            setPlan(localStorage.getItem("plan"));
        };

        window.addEventListener('storage', handleStorageChange);
        window.addEventListener('planStatusChanged', handleStorageChange);

        return () => {
            window.removeEventListener('storage', handleStorageChange);
            window.removeEventListener('planStatusChanged', handleStorageChange);
        };
    }, []);

    const getData = (page, showLoader = true) => {
        if (showLoader) setLoading(true);
        const params = {
            page: page,
            limit: rowsPerPage,
        };

        getInvoicesAPI(params)
            .then((res) => {
                setCurrentInvoices(res?.data?.data?.subscriptions || []);
                setPageCount(res?.data?.total_pages || 0);
                localStorage.setItem("plan_start_date", res?.data?.data?.current_plan?.current_period_start);
                localStorage.setItem("plan_end_date", res?.data?.data?.current_plan?.current_period_end);
            })
            .catch((err) => {
                const message = err?.response?.data?.message;
                if (err?.response?.status === 401) {
                    localStorage.removeItem('hoast_token')
                    localStorage.removeItem('hoast_refresh_token')
                    navigate('/login');
                }
                toast.error(message || "Something went wrong!");
            })
            .finally(() => {
                if (showLoader) setLoading(false);
            });
    };

    const getCurrentPlanData = () => {
        setLoading(true);
        getCheckoutSubscriptionPlanAPI()
            .then((res) => {
                setCurrentPlanData(res?.data?.data?.available_plans);
            })
            .catch((err) => {
                const message = err?.response?.data?.message;
                if (err?.response?.status === 401) {
                    localStorage.removeItem('hoast_token')
                    localStorage.removeItem('hoast_refresh_token')
                    navigate('/login');
                }
                toast.error(message || "Something went wrong!");
            })
            .finally(() => {
                setLoading(false);
            });
    };

    useEffect(() => {
        getData(currentPage, true);
    }, [currentPage, rowsPerPage]);

    useEffect(() => {
        getCurrentPlanData();
    }, []);

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    const handlePlanUpgrade = (plan) => {
        setIsPaymentLoading(true);
        checkoutSubscriptionAPI({
            user_id: userId,
            stripe_product_id: plan,
        })
            .then((res) => {
                if (res?.data?.status === 1) {
                    const checkoutUrl = res?.data?.data?.url;
                    window.location.href = checkoutUrl;
                } else {
                    toast.error(res?.data?.message);
                }
            })
            .catch((err) => {
                const message = err?.response?.data?.message;
                toast.error(message || "Something went wrong!");
            })
            .finally(() => {
                setIsPaymentLoading(false);
            });
    };

    const renderPaginationButtons = () => {
        const buttons = [];
        for (let i = 1; i <= pageCount; i++) {
            buttons.push(
                <button
                    key={i}
                    className={`rounded-full h-9 w-9 font-bold text-base box border border-[#EDEDED] mx-1 ${currentPage === i ? 'bg-[#050840] text-white' : ''
                        }`}
                    onClick={() => handlePageChange(i)}
                >
                    {i}
                </button>
            );
        }
        return buttons;
    };

    const viewUnvoice = (invoice) => {
        navigate(`/invoice/${invoice.id}`, { state: { invoice: invoice } })
    };

    const handlePlanChange = () => {
        setCancelPlan(false);
        setPlan(localStorage.setItem("plan", "no"));
        window.dispatchEvent(new Event('planStatusChanged'));
    };

    return (
        <div className="md:p-5 p-3 bg-[#FFFFFF] rounded-[12px]">
            <h2 className="text-[#0F1113] text-[20px] sm:text-[24px] font-semibold mb-4">Billing</h2>

            {plan == "yes" &&
                <div>
                    {loading ?
                        <div>
                            <Skeleton height={24} width={120} className="mb-6" />

                            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6 mb-10">
                                <div className="border border-[#24B565] bg-[#24B5650D] rounded-xl p-6 py-8 shadow-sm relative">
                                    <div className="absolute top-5 right-4">
                                        <Skeleton width={20} height={20} borderRadius="50%" />
                                    </div>
                                    <div className="space-y-2">
                                        <Skeleton height={32} width={120} />
                                        <Skeleton height={16} width={200} />
                                    </div>
                                    <div className="mt-6">
                                        <Skeleton height={48} width="100%" className="sm:w-[200px]" />
                                    </div>
                                </div>

                                <div className="bg-gradient-to-r from-[#508BFF] to-[#1D64F0] rounded-xl p-6 py-8 shadow-sm">
                                    <div className="space-y-2">
                                        <Skeleton height={32} width={120} className="bg-white/20" />
                                        <Skeleton height={16} width={200} className="bg-white/20" />
                                    </div>
                                    <div className="mt-6">
                                        <Skeleton height={48} width="100%" className="sm:w-[200px] bg-white/20" />
                                    </div>
                                </div>
                                <div className="bg-gradient-to-r from-[#508BFF] to-[#1D64F0] rounded-xl p-6 py-8 shadow-sm">
                                    <div className="space-y-2">
                                        <Skeleton height={32} width={120} className="bg-white/20" />
                                        <Skeleton height={16} width={200} className="bg-white/20" />
                                    </div>
                                    <div className="mt-6">
                                        <Skeleton height={48} width="100%" className="sm:w-[200px] bg-white/20" />
                                    </div>
                                </div>
                            </div>
                        </div>
                        :
                        <div>
                            <h3 className="text-lg font-medium text-[#383E49] mb-6">Current Plan</h3>
                            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6 mb-10">
                                {currentPlanData?.map((planItem) => {
                                    const billingPeriod = planItem.billing_period === "year" ? "/yr" :
                                        planItem.billing_period === "month" ? "/mo" :
                                            planItem.billing_period === "day" ? "/day" : "";
                                    const isCurrentPlan = planItem.action_type === "current";
                                    const isUpgrade = planItem.action_type === "upgrade";

                                    if (isCurrentPlan) {
                                        return (
                                            <div key={planItem.id} className="border border-[#24B565] bg-[#24B5650D] rounded-xl p-6 py-8 flex flex-col justify-between shadow-sm relative">
                                                <div className="absolute top-5 right-4">
                                                    <div className="w-5 h-5 bg-[#24B565] rounded-full flex items-center justify-center">
                                                        <Check size={16} className="text-white" />
                                                    </div>
                                                </div>
                                                <div>
                                                    {planItem.amount === 0 ? (
                                                        <p className="md:text-3xl text-xl font-semibold text-[#1E1E1E]">Free</p>
                                                    ) : (
                                                        <p className="md:text-2xl text-xl font-semibold text-[#1E1E1E]">
                                                            ${planItem.amount.toFixed(2)}
                                                            <span className="text-sm text-[#667085] font-normal">{billingPeriod}</span>
                                                        </p>
                                                    )}
                                                    <p className="text-sm text-[#383E49] font-medium mt-1">
                                                        {planItem.plan_name} {planItem.plan_type !== "free_tier" && "Active"}
                                                    </p>
                                                    <p className="md:text-sm text-xs text-[#383e49ac] font-medium mt-1">
                                                        Start Date: <span className="font-bold text-black">{moment(startDate).format("MM/DD/YYYY")}</span>
                                                    </p>
                                                    <p className="md:text-sm text-xs text-[#383e49ac] font-medium mt-1">
                                                        Expiration Date: <span className="font-bold text-black">{moment(endDate).format("MM/DD/YYYY")}</span>
                                                    </p>
                                                    {planItem.days_remaining !== null && (
                                                        <p className="md:text-sm text-xs text-[#383e49ac] font-medium mt-1">
                                                            Days Remaining: <span className="font-bold text-[#24B565]">{planItem.days_remaining} days</span>
                                                        </p>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    }

                                    if (isUpgrade) {
                                        return (
                                            <div key={planItem.id} className="bg-gradient-to-r from-[#508BFF] to-[#1D64F0] text-white rounded-xl p-6 py-8 flex flex-col justify-between shadow-sm relative">
                                                {planItem.plan_desc && (
                                                    <button
                                                        className="absolute top-5 right-4"
                                                        onClick={() => {
                                                            setSelectedPlanInfo(planItem);
                                                            setShowInfoModal(true);
                                                        }}
                                                    >
                                                        <BsInfoCircleFill className="text-white text-xl" />
                                                    </button>
                                                )}
                                                <div>
                                                    {planItem.amount === 0 ? (
                                                        <p className="md:text-3xl text-xl font-semibold">Free</p>
                                                    ) : (
                                                        <p className="md:text-2xl text-xl font-semibold">
                                                            ${planItem.amount.toFixed(2)}
                                                            <span className="text-sm font-normal">{billingPeriod}</span>
                                                        </p>
                                                    )}
                                                    <p className="text-sm font-medium mt-1">{planItem.plan_name}</p>
                                                </div>
                                                <div className="mt-6">
                                                    <button
                                                        onClick={() => {
                                                            handlePlanUpgrade(planItem.stripe_product_id);
                                                            setSelectedPlan(planItem.stripe_product_id);
                                                        }}
                                                        className="w-full bg-[#212121] md:w-[200px] text-white text-sm px-5 py-3 rounded-[12px] shadow-xl hover:opacity-90"
                                                    >
                                                        {isPaymentLoading && selectedPlan === planItem.stripe_product_id ? (
                                                            <EllipsisLoader color="white" />
                                                        ) : (
                                                            isUpgrade ? "Upgrade" : "Downgrade"
                                                        )}
                                                    </button>
                                                </div>
                                            </div>
                                        );
                                    }
                                    return null;
                                })}
                            </div>
                        </div>
                    }
                </div>
            }

            <h3 className="text-lg font-medium text-[#383E49] mb-6">Invoice History</h3>

            {loading ? (
                <div className="space-y-6">
                    <div className="overflow-auto text-sm">
                        <div className="w-full border-collapse">
                            <div className="shadow-sm bg-[#f9f9f9] py-4 px-3">
                                <div className="grid grid-cols-5 gap-4">
                                    <Skeleton height={20} width="80%" />
                                    <Skeleton height={20} width="70%" />
                                    <Skeleton height={20} width="60%" />
                                    <Skeleton height={20} width="50%" />
                                    <Skeleton height={20} width="60%" />
                                </div>
                            </div>

                            {Array.from({ length: rowsPerPage }).map((_, i) => (
                                <div key={i} className="bg-white border-t py-4 px-3">
                                    <div className="grid grid-cols-5 gap-4">
                                        <Skeleton height={16} width="90%" />
                                        <Skeleton height={16} width="80%" />
                                        <Skeleton height={16} width="85%" />
                                        <Skeleton height={16} width="70%" />
                                        <Skeleton height={16} width="60%" />
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="flex px-5 items-center justify-between my-3">
                        <div>
                            <Skeleton height={40} width={100} />
                        </div>
                        <div className="flex items-center space-x-2">
                            <Skeleton height={36} width={36} borderRadius="50%" />
                            <div className="flex space-x-1">
                                {Array.from({ length: 3 }).map((_, i) => (
                                    <Skeleton key={i} height={36} width={36} borderRadius="50%" />
                                ))}
                            </div>
                            <Skeleton height={36} width={36} borderRadius="50%" />
                        </div>
                    </div>
                </div>
            ) : currentInvoices.length > 0 ? (
                <div>
                    <div className="overflow-auto text-sm">
                        <Table>
                            <Table.Thead>
                                <Table.Tr className="shadow-sm text-[#667085] py-4">
                                    <Table.Th className="border-b-0 whitespace-nowrap font-medium h-[50px]">
                                        DATE
                                    </Table.Th>
                                    <Table.Th className="border-b-0 whitespace-nowrap font-medium">
                                        INVOICE
                                    </Table.Th>
                                    <Table.Th className="border-b-0 whitespace-nowrap font-medium">
                                        DETAILS
                                    </Table.Th>
                                    <Table.Th className="border-b-0 whitespace-nowrap font-medium">
                                        AMOUNT
                                    </Table.Th>
                                    <Table.Th className="border-b-0 whitespace-nowrap font-medium">
                                        DOWNLOAD
                                    </Table.Th>
                                </Table.Tr>
                            </Table.Thead>

                            <Table.Tbody>
                                {currentInvoices.map((invoice, index) => (
                                    <Table.Tr className="text-[#4B4B4B]" key={invoice.id}>
                                        <Table.Td className="first:rounded-l-md last:rounded-r-md bg-white whitespace-nowrap py-4">
                                            <div>{moment(invoice?.current_period_start).format("MM/DD/YYYY")}</div>
                                        </Table.Td>
                                        <Table.Td className="first:rounded-l-md last:rounded-r-md bg-white whitespace-nowrap">
                                            <div>{invoice?.stripe_invoice_id}</div>
                                        </Table.Td>
                                        <Table.Td className="first:rounded-l-md last:rounded-r-md bg-white whitespace-nowrap">
                                            <div>{invoice?.plan_name || "-"}</div>
                                        </Table.Td>
                                        <Table.Td className="first:rounded-l-md last:rounded-r-md bg-white whitespace-nowrap">
                                            <div>${invoice?.amount}</div>
                                        </Table.Td>
                                        <Table.Td className="first:rounded-l-md last:rounded-r-md bg-white whitespace-nowrap">
                                            <button
                                                onClick={() => {
                                                    if (invoice?.plan_name === 'Free Trial') {
                                                        viewUnvoice(invoice)
                                                    } else {
                                                        window.open(invoice?.invoice_url, '_blank')
                                                    }

                                                }}
                                                className="flex items-center gap-2 text-blue-600 hover:text-blue-800 transition-colors"
                                            >
                                                <span className="text-sm">View Invoice</span>
                                            </button>
                                        </Table.Td>
                                    </Table.Tr>
                                ))}
                            </Table.Tbody>
                        </Table>
                    </div>

                    <div className="flex items-center justify-between my-3">
                        <div>
                            <FormSelect
                                value={rowsPerPage}
                                onChange={(e) => {
                                    setRowsPerPage(Number(e.target.value));
                                    setCurrentPage(1);
                                }}
                                className="px-3 text-black bg-[#F8F8F8] outline-none"
                            >
                                <option value="5">Rows 5</option>
                                <option value="10">Rows 10</option>
                                <option value="20">Rows 20</option>
                                <option value="50">Rows 50</option>
                            </FormSelect>
                        </div>

                        <Pagination>
                            <Pagination.Link>
                                <button
                                    className='rounded-full p-1 font-bold text-base box border border-[#EDEDED]'
                                    onClick={() => handlePageChange(currentPage - 1)}
                                    disabled={currentPage === 1}
                                >
                                    <ChevronLeft />
                                </button>
                            </Pagination.Link>
                            <div className="flex items-center">
                                {renderPaginationButtons()}
                            </div>
                            <Pagination.Link>
                                <button
                                    className='rounded-full p-1 font-bold text-base box border border-[#EDEDED]'
                                    onClick={() => handlePageChange(currentPage + 1)}
                                    disabled={currentPage === pageCount}
                                >
                                    <ChevronRight />
                                </button>
                            </Pagination.Link>
                        </Pagination>
                    </div>
                </div>
            ) :
                <div className="flex justify-center items-center text-center w-full h-[40vh] mt-8">
                    <p className="text-center text-base font-bold ">
                        No Data Found
                    </p>
                </div>
            }

            <Dialog
                open={cancelPlan}
                onClose={() => setCancelPlan(false)}
                size="md"
            >
                <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
                <Dialog.Panel>
                    <div className='flex justify-end mt-3'>
                        <button
                            className="bg-[#F4F5FA] text-sm rounded-xl sm:mx-3 mx-2"
                            onClick={() => setCancelPlan(false)}
                        >
                            <X />
                        </button>
                    </div>
                    <Dialog.Description>
                        <div className="flex justify-center text-center px-8">
                            <div>
                                <h1 className="text-2xl font-semibold text-black">
                                    Cancel Subscription?
                                </h1>
                                <p className="md:text-base text-sm text-gray-500 my-3">
                                    Are you sure you want to cancel your subscription? You'll lose premium access after this period.
                                </p>
                                <div className="flex items-center my-8 justify-center">
                                    <button
                                        onClick={handlePlanChange}
                                        className="bg-[#EA5F5F] h-[50px] w-full text-sm rounded-xl sm:mx-3 mx-2 text-white"
                                    >
                                        Yes, Cancel
                                    </button>
                                </div>
                            </div>
                        </div>
                    </Dialog.Description>
                </Dialog.Panel>
            </Dialog>

            <Dialog
                open={showInfoModal}
                onClose={() => {
                    setShowInfoModal(false);
                }}
                size="md"
            >
                <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
                <Dialog.Panel>
                    <div className='flex justify-between items-center px-8 my-5'>
                        <h1 className="text-xl font-semibold text-black">{selectedPlanInfo?.plan_name || "Plan Details"}</h1>

                        <button
                            className="bg-[#F4F5FA] text-sm rounded-xl sm:mx-3 mx-2 max-w-6 h-6 max-h-6 p-0.5"
                            onClick={() => {
                                setShowInfoModal(false);
                            }}
                        >
                            <X className="h-full w-full" />
                        </button>
                    </div>
                    <Dialog.Description>
                        <div className="flex justify-center text-center px-8">
                            <div>
                                <div className='text-start'>
                                    {selectedPlanInfo?.amount === 0 ? (
                                        <p className="md:text-3xl text-xl font-semibold">Free</p>
                                    ) : (
                                        <p className="md:text-2xl text-xl font-semibold">
                                            ${selectedPlanInfo?.amount.toFixed(2)}
                                            <span className="text-sm font-normal">
                                                {selectedPlanInfo?.billing_period === "year" ? "/yr" :
                                                    selectedPlanInfo?.billing_period === "month" ? "/mo" :
                                                        selectedPlanInfo?.billing_period === "day" ? "/day" : ""}
                                            </span>
                                        </p>
                                    )}
                                </div>
                                <p className="md:text-base text-sm text-black text-start my-3">
                                    {selectedPlanInfo?.plan_desc || "No description available."}
                                </p>
                            </div>
                        </div>
                    </Dialog.Description>
                </Dialog.Panel>
            </Dialog>
        </div>
    );
}
